/**
 * Analytics Validators
 * Validates event payloads and data structure integrity
 */

import { ANALYTICS_CONFIG } from './constants.js';

/**
 * Validate event name
 * @param {string} event_name - Event name to validate
 * @returns {boolean} - True if valid
 */
export function validate_event_name(event_name) {
  if (typeof event_name !== 'string' || event_name.trim() === '') {
    warn_validation('[Analytics] Missing event name');
    return false;
  }

  if (!/^[a-z_]+$/.test(event_name)) {
    warn_validation(`[Analytics] Invalid event name: ${event_name}. Use snake_case only.`);
    return false;
  }

  return true;
}

/**
 * Validate payload structure
 * @param {Object} payload - Payload to validate
 * @returns {boolean} - True if valid
 */
export function validate_payload_structure(payload) {
  if (!payload || typeof payload !== 'object') {
    warn_validation('[Analytics] Payload must be an object');
    return false;
  }

  if (Array.isArray(payload)) {
    warn_validation('[Analytics] Payload cannot be an array');
    return false;
  }

  return true;
}

/**
 * Validate for PII (personally identifiable information)
 * @param {Object} obj - Object to check for PII
 * @param {string} path - Current path in object (for error reporting)
 * @returns {boolean} - True if no PII detected
 */
export function validate_no_pii(obj, path = 'root') {
  const pii_patterns = {
    email: /@/,
    phone: /\d{3}[-.]?\d{3}[-.]?\d{4}/,
    ssn: /\d{3}-\d{2}-\d{4}/,
    name: /^(first_name|last_name|full_name|user_name)$/i,
    password: /password/i
  };

  for (const [key, value] of Object.entries(obj)) {
    const current_path = `${path}.${key}`;

    // Check key names
    for (const [pii_type, pattern] of Object.entries(pii_patterns)) {
      if (pattern.test(key)) {
        warn_validation(`[Analytics] Potential PII detected at ${current_path}`);
        return false;
      }
    }

    // Recursively check nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (!validate_no_pii(value, current_path)) {
        return false;
      }
    }

    // Check string values for email patterns
    if (typeof value === 'string' && value.includes('@')) {
      warn_validation(`[Analytics] Potential email detected at ${current_path}`);
      return false;
    }
  }

  return true;
}

/**
 * Validate required event properties
 * @param {Object} payload - Event payload
 * @param {Array<string>} required_fields - Required field names
 * @returns {boolean} - True if all required fields present
 */
export function validate_required_fields(payload, required_fields = []) {
  for (const field of required_fields) {
    if (!(field in payload)) {
      warn_validation(`[Analytics] Missing required field: ${field}`);
      return false;
    }

    if (payload[field] === null || payload[field] === undefined || payload[field] === '') {
      warn_validation(`[Analytics] Required field is empty: ${field}`);
      return false;
    }
  }

  return true;
}

/**
 * Validate complete event payload
 * @param {string} event_name - Event name
 * @param {Object} payload - Event payload
 * @returns {boolean} - True if valid
 */
export function validate_event(event_name, payload = {}) {
  // Validate event name
  if (!validate_event_name(event_name)) {
    return false;
  }

  // Validate payload structure
  if (!validate_payload_structure(payload)) {
    return false;
  }

  // Validate no PII
  if (!validate_no_pii(payload)) {
    return false;
  }

  return true;
}

/**
 * Internal warning function
 * Only logs in development mode
 * @param {string} message - Warning message
 */
function warn_validation(message) {
  if (ANALYTICS_CONFIG.debug && typeof console !== 'undefined') {
    console.warn(message);
  }
}
